/**
 * Navbar Component
 * Main navigation bar with responsive mobile menu.
 * Menu data: login-app/src/config/nav-menu.json (synced to public/js for static pages).
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  NAV_MENU_ITEMS,
  isInternalStaff,
  isItemRestricted,
  isItemComingSoon,
  groupByCategory,
} from '../../config/navMenu';
import './Navbar.css';
import ThemeToggle from './ThemeToggle';
import { isRfIcon, rfIconStyle } from '../../config/icons';

/** A Rapid Fusion icon (plum) when the name is in the set, otherwise the Font Awesome one. */
const NavIcon = ({ name, className = '' }) => (isRfIcon(name)
  ? <span className={`rf-icon ${className}`} style={rfIconStyle(name)} aria-hidden="true" />
  : <i className={`fas fa-${name || 'circle'} ${className}`} aria-hidden="true"></i>);

const Navbar = ({ isAuthenticated = false, currentUser = null, userRole = null, onLogout }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const navRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    setActiveDropdown(null);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  const toggleDropdown = (e, dropdownId) => {
    e.preventDefault();
    setActiveDropdown(activeDropdown === dropdownId ? null : dropdownId);
  };

  const isActive = (path) => location.pathname === path;

  const itemRestricted = (item) => isItemRestricted(item, userRole);

  const groupItems = (items) => groupByCategory(items, userRole);

  const renderDropdownItem = (item) => {
    const isExternal = item.url.startsWith('http');
    const content = (
      <>
        <span className="rf-mega-item-icon"><NavIcon name={item.icon} /></span>
        <span className="rf-mega-item-text">
          <span className="rf-mega-item-label">{item.label}</span>
          {item.description && <span className="rf-mega-item-desc">{item.description}</span>}
        </span>
      </>
    );

    if (isItemComingSoon(item, userRole)) {
      return (
        <span
          key={item.url}
          className="rf-nav-dropdown-item rf-mega-item rf-nav-dropdown-item-disabled"
          aria-disabled="true"
        >
          {content}
          <span className="rf-nav-coming-soon">Coming soon</span>
        </span>
      );
    }

    if (isExternal) {
      return (
        <a
          key={item.url}
          href={item.url}
          className="rf-nav-dropdown-item rf-mega-item"
          target="_blank"
          rel="noopener noreferrer"
          onClick={closeMobileMenu}
        >
          {content}
        </a>
      );
    }

    return (
      <Link
        key={item.url}
        to={item.url}
        className={`rf-nav-dropdown-item rf-mega-item ${isActive(item.url) ? 'active' : ''}`}
        onClick={closeMobileMenu}
      >
        {content}
      </Link>
    );
  };

  const renderMenuItem = (item) => {
    if (itemRestricted(item)) return null;

    if (item.dropdown) {
      const groupedItems = groupItems(item.items);
      const hasItems = groupedItems.some((group) => group.items.length > 0);
      if (!hasItems) return null;
      return (
        <li
          key={item.id}
          className={`rf-nav-item rf-nav-dropdown rf-nav-mega ${activeDropdown === item.id ? 'active' : ''}`}
        >
          <a
            href="#"
            className="rf-nav-link rf-nav-dropdown-toggle"
            onClick={(e) => toggleDropdown(e, item.id)}
          >
            <NavIcon name={item.icon} className="rf-nav-link-icon" />
            <span>{item.label}</span>
          </a>
          <div className="rf-nav-dropdown-menu rf-mega">
            <div className="rf-mega-intro">
              <NavIcon name={item.icon} />
              <h3>{item.label}</h3>
              {item.description && <p>{item.description}</p>}
            </div>
            <div className="rf-mega-groups">
              {groupedItems.map((group) => (
                <div className="rf-nav-dropdown-group rf-mega-group" key={group.category || 'default'}>
                  {group.category && (
                    <div className="rf-nav-dropdown-category">{group.category}</div>
                  )}
                  {group.items.map((dropItem) => renderDropdownItem(dropItem))}
                </div>
              ))}
            </div>
          </div>
        </li>
      );
    }

    const isExternalHtml = item.path && item.path.endsWith('.html');

    return (
      <li key={item.id} className="rf-nav-item">
        {isExternalHtml ? (
          <a
            href={item.path}
            className={`rf-nav-link ${isActive(item.path) ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            <NavIcon name={item.icon} className="rf-nav-link-icon" />
            <span>{item.label}</span>
          </a>
        ) : (
          <Link
            to={item.path}
            className={`rf-nav-link ${isActive(item.path) ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            <NavIcon name={item.icon} className="rf-nav-link-icon" />
            <span>{item.label}</span>
          </Link>
        )}
      </li>
    );
  };

  return (
    <nav className="rf-navbar" ref={navRef}>
      <div className="rf-nav-container">
        <div className="rf-nav-side">
          <Link to="/" className="rf-nav-logo" onClick={closeMobileMenu}>
            <span className="rf-nav-logo-text">Rapid Fusion</span>
          </Link>
        </div>

        <ul className={`rf-nav-menu ${mobileMenuOpen ? 'active' : ''}`}>
          {isAuthenticated && NAV_MENU_ITEMS.map((item) => renderMenuItem(item))}

          {isAuthenticated && isInternalStaff(userRole) && (
            <li className="rf-nav-item rf-nav-admin">
              <Link
                to="/admin/access"
                className={`rf-nav-link ${isActive('/admin/access') ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                <NavIcon name="key" className="rf-nav-link-icon" />
                <span>{userRole === 'admin' ? 'Admin' : 'Users'}</span>
              </Link>
            </li>
          )}
        </ul>

        <div className="rf-nav-side rf-nav-side-end">
          <button
            className="rf-mobile-menu-toggle"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <i className={`fas fa-${mobileMenuOpen ? 'times' : 'bars'}`}></i>
          </button>
          <div className={`rf-nav-actions ${mobileMenuOpen ? 'mobile-visible' : ''}`}>
          <ThemeToggle />
          {isAuthenticated ? (
            <div className={`rf-nav-item rf-nav-dropdown rf-nav-account ${activeDropdown === 'account' ? 'active' : ''}`}>
              <button
                type="button"
                className="rf-nav-link rf-nav-dropdown-toggle rf-nav-account-toggle"
                aria-haspopup="true"
                aria-expanded={activeDropdown === 'account'}
                onClick={(e) => toggleDropdown(e, 'account')}
              >
                <i className="fas fa-user"></i>
                <span>{currentUser || 'Account'}</span>
              </button>
              <div className="rf-nav-dropdown-menu rf-nav-account-menu">
                <Link to="/change-password" className="rf-nav-dropdown-item" onClick={closeMobileMenu}>
                  <i className="fas fa-key rf-nav-dropdown-item-icon"></i>
                  <span>Change Password</span>
                </Link>
                <button type="button" className="rf-nav-dropdown-item" onClick={onLogout}>
                  <i className="fas fa-sign-out-alt rf-nav-dropdown-item-icon"></i>
                  <span>Log Out</span>
                </button>
              </div>
            </div>
          ) : (
            <>
              <Link to="/request-access" className="rf-btn rf-btn-outline rf-btn-sm" onClick={closeMobileMenu}>
                <i className="fas fa-user-plus"></i>
                Request Access
              </Link>
              <Link to="/login" className="rf-btn rf-btn-primary rf-btn-sm" onClick={closeMobileMenu}>
                <i className="fas fa-sign-in-alt"></i>
                Log In
              </Link>
            </>
          )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
